import { clsx } from 'clsx';
import { DeletePopup } from 'src/components/DeletePopup/DeletePopup';
import { DeleteIconSimple } from 'src/assets/icons/deleteIconSimple';
import { useParams } from 'react-router-dom';
import { useGetProjectByIdQuery } from 'src/services';
import { INTEGRATION_SETTING_POPUP_ID } from 'src/shared/constants';
import { Button } from 'src/components/ui/button';
import { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from 'src/store';
import { DialogTrigger } from 'src/components/ui/dialog';
import { IntegrationsModal } from 'src/pages/ControlDeskPage/IntegrationStatusSection/IntegrationsModal/IntegrationsModal';
import { setSelectedIntegration } from 'src/store/slices/integrationSlice';
import { EditIntegrationPopup } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationPopup';
import { Separator } from 'src/components/ui/separator';
import { CrossCircleIcon } from 'src/assets/icons/crossCircleIcon';
import { EditIconSettings } from 'src/assets/icons/editIconsSettings';
import { SettingsPopoverButton } from 'src/components/SettingsPopoverButton/SettingsPopoverButton';
import type { CurrentIntegrationCardProps } from './currentIntegrationCardProps';

// An integration on the control desk: its name and icon, a Launch button while its setup is
// unfinished, and a menu with Settings, Deactivate and Delete.
export const CurrentIntegrationCard = ({
  integrationTypeConfig,
  integration,
  deleteIntegrationHandler,
  disabledByPermision = false,
  launchStep,
  keepSelectionInSync = false,
}: CurrentIntegrationCardProps & {
  // The setup step Launch opens at; integrations without a setup flow have none.
  launchStep?: number;
  // Re-select the integration whenever its data changes, rather than only when first shown.
  keepSelectionInSync?: boolean;
}) => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { isInactivePlan } = useAppSelector((s) => s.planSlice);
  const { data: currentProject } = useGetProjectByIdQuery({ id: id });
  // Actions are disabled once the project's data shows it inactive.
  const disabled = Boolean(currentProject) && !currentProject.is_active;
  const openIntegrationId = localStorage.getItem(INTEGRATION_SETTING_POPUP_ID);
  const isOpen = openIntegrationId && integration.id === +openIntegrationId;
  const hasSetupFlow = launchStep !== undefined;
  // Without a setup flow, the integration counts as set up.
  const isSetupDone = !hasSetupFlow || integration?.integration_data?.is_setup;

  useEffect(() => {
    dispatch(setSelectedIntegration({ ...integrationTypeConfig, ...integration }));
    return () => {
      dispatch(setSelectedIntegration(null));
    };
  }, [keepSelectionInSync ? integration : null]);

  return (
    <div className={clsx('transition flex justify-between items-center')}>
      <div className="flex items-center gap-2.5">
        <div className="w-fit">
          {integrationTypeConfig.icon && (
            <img className="h-[30px] w-[30px]" src={integrationTypeConfig.icon} />
          )}
        </div>
        <div className="flex items-center gap-2.5">
          <p className="font-medium font-jamjuree text-sm text-ebony opacity-80">
            {integrationTypeConfig.friendlyName || ''}
          </p>
        </div>
      </div>
      <div>
        <div className={'flex flex-row gap-4 items-center'}>
          {!isSetupDone && (
            <IntegrationsModal
              trigger={
                <DialogTrigger asChild>
                  <Button
                    disabled={disabled || isInactivePlan}
                    variant="outline"
                    className="w-full justify-start shadow-[0_2px_2px_0_rgba(0,0,0,0.25)] gap-1.5"
                  >
                    <p className="font-medium text-base-xs">Launch</p>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="13"
                      height="14"
                      viewBox="0 0 13 14"
                      fill="none"
                    >
                      <path
                        d="M2.52547 7.16976L0.854124 5.49841L1.27196 5.08058C2.52547 3.82707 4.61465 4.24491 4.61465 4.24491L6.70383 2.15573C8.37517 0.484385 11.9579 1.20497 11.9579 1.20497C11.9579 1.20497 12.5535 4.66274 10.8822 6.33409L8.793 8.42327C8.793 8.42327 9.21084 10.5124 7.95733 11.766L7.5395 12.1838L5.86815 10.5124M2.52547 7.16976L2.10763 9.25894L3.77898 10.9303L5.86815 10.5124M2.52547 7.16976L5.86815 10.5124M8.793 4.24491C8.90382 4.35572 9.05412 4.41798 9.21084 4.41798C9.36756 4.41798 9.51786 4.35572 9.62868 4.24491C9.73949 4.13409 9.80175 3.98379 9.80175 3.82707C9.80175 3.67035 9.73949 3.52005 9.62868 3.40924C9.51786 3.29842 9.36756 3.23616 9.21084 3.23616C9.05412 3.23616 8.90382 3.29842 8.793 3.40924C8.68219 3.52005 8.61993 3.67035 8.61993 3.82707C8.61993 3.98379 8.68219 4.13409 8.793 4.24491Z"
                        stroke="#05050D"
                        strokeOpacity="0.8"
                        strokeWidth="1.18182"
                      />
                    </svg>
                  </Button>
                </DialogTrigger>
              }
              initialStep={launchStep}
            />
          )}
          <SettingsPopoverButton
            disabledByPermision={disabledByPermision}
            content={
              <div className="flex flex-col items-center sm:justify-end justify-between">
                <div className="flex flex-col w-full">
                  <EditIntegrationPopup
                    isOpen={isOpen}
                    integration={integration}
                    trigger={
                      <Button variant="ghost" className="w-full justify-start !rounded-none">
                        <div className="flex items-center gap-2.5">
                          <EditIconSettings />
                          <p className="opacity-80 font-medium text-sm">Settings</p>
                        </div>
                      </Button>
                    }
                  />
                  <Separator className={'bg-[#E3E6E8]'} />
                </div>
                <div className="flex flex-col w-full">
                  <div className="flex items-center gap-2.5">
                    {isSetupDone && (
                      <Button
                        disabled
                        variant="ghost"
                        className="w-full justify-start !rounded-none"
                      >
                        <div className="flex items-center gap-2.5">
                          <CrossCircleIcon />
                          <p className="opacity-80 font-medium text-sm">Deactivate</p>
                        </div>
                      </Button>
                    )}
                  </div>
                  <Separator className={'bg-[#E3E6E8]'} />
                </div>

                <div className="flex flex-col w-full">
                  <DeletePopup
                    disabled={disabled}
                    deleteHandler={() => deleteIntegrationHandler(integration.id)}
                    description={`Are you sure you want to delete the integration?`}
                    triggerIcon={
                      <Button variant="ghost" className="w-full justify-start !rounded-none">
                        <div className="flex items-center gap-2.5">
                          <div className="[&_svg>path]:fill-error">
                            <DeleteIconSimple />
                          </div>
                          <p className="opacity-80 font-medium text-sm text-error">Delete</p>
                        </div>
                      </Button>
                    }
                  />
                </div>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
};
